import {FlatTicket, FlatTicketOptions} from '@/components/flat-ticket';
import CircleSlider from '@/components/circle-slider';
import ConstructorWidget from '@/components/constructor-widget';
import CustomSelect from '@/components/form/custom-select';
import DesignSlider from '@/components/design-slider';
import DetailsAccordion from '@/components/details';
import FavoriteButton from '@/components/favorite-button';
import FavoritesList from '@/components/favorites';
import FavoriteState from '@/components/favorite-state';
import FlatConstructor from '@/components/constructor';
import Form from '@/components/form';
import FullScreenSlider from '@/components/full-screen-slider';
import Infrastructure from '@/sections/infrastructure';
import InitPlanoplan from '@/components/planoplan';
import Menu from '@/components/menu';
import MenuTrigger from '@/components/menu/menuTrigger';
import MortgageCalculator from '@/components/mortgage-calculator';
import Observer from './observer';
import OffersList from '@/sections/offers';
import ParallaxMouse from '@/components/parallaxMouse';
import Parametric from '@/components/parametric';
import {PopupFacade} from '@/components/popup/popupFacade';
import Preloader from '@/components/preloader';
import SwiperSlider from '@/components/swiper-slider';
import Tabs from '@/components/tabs';
import Toggle from '@/components/toggle';
import {Utils} from './utils';
import WhiteBox from '../../components/white-box';

const observer = new Observer();

/* Подключение ленивой загрузки (img/picture/iframe) */
export const lazyLoadElements = (lazyElements) => {
    const replaceSource = (element) => {
        const targetAttribute = element.nodeName === 'SOURCE' ? 'srcset' : 'src';
        const sourceElement = element.nodeName === 'SOURCE' ? 'data-srcset' : 'data-src';

        if (element.getAttribute(sourceElement)) {
            element.setAttribute(targetAttribute, element.getAttribute(sourceElement));
            element.removeAttribute(sourceElement);

            if (element.parentElement.tagName === 'PICTURE' && element.tagName === 'IMG') {
                setTimeout(function tick() {
                    if (element.src) {
                        Utils.loadHandler(element.src)
                            .then(() => {
                                element.parentElement.style.backgroundImage = '';
                            });
                    } else {
                        setTimeout(tick, 100);
                    }
                }, 100);
            }
        }
    };

    if ('loading' in HTMLImageElement.prototype || !('IntersectionObserver' in window)) {
        // Если есть нативная поддержка атрибута или нет поддержки IntersectionObserver - подменяем сразу
        lazyElements.forEach((lazyEl) => {
            replaceSource(lazyEl);
        });
    } else {
        // Иначе по событию IntersectionObserver
        const lazyImageObserver = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting) {
                    const lazyEl = entry.target;

                    replaceSource(lazyEl);
                    lazyImageObserver.unobserve(lazyEl);
                }
            });
        });

        lazyElements.forEach((lazyImage) => {
            if (Utils.isInViewport(lazyImage, window.innerHeight || document.documentElement.clientHeight)) {
                replaceSource(lazyImage);
            } else {
                lazyImageObserver.observe(lazyImage);
            }
        });
    }
};

export const initVideoTabs = (wrapperSelector) => {
    const wrapper = document.querySelector(wrapperSelector);

    if (wrapper) {
        const tabs = Array.from(wrapper.querySelectorAll('.j-video__tab'));
        const iframe = wrapper.querySelector('.j-video__iframe');
        const isActive = 'is-active';

        tabs.forEach((tab) => {
            tab.addEventListener('click', (event) => {
                const target = event.currentTarget;

                iframe.src = target.dataset.video;
                tabs.forEach((element) => {
                    const method = element === target ? 'add' : 'remove';

                    element.classList[method](isActive);
                });
            });
        });
    }
};


// Установка размеров видео первого экрана
export const initFullVideo = (fullIframe) => {
    const wrapper = document.querySelector('.j-first-screen__video');
    const ratio = 0.5625;

    const setVideoSize = () => {
        const width = Utils.getWindowWidth();
        const height = Utils.isMobile() ? wrapper.offsetHeight : Utils.getWindowHeight();
        const dimension = height / width;

        if (dimension > ratio) {
            fullIframe.style.width = `${height / ratio}px`;
            fullIframe.style.height = `${height + Math.abs(width - (height / ratio))}px`;
            fullIframe.style.top = `${0.5 * (width - (height / ratio))}px`;
            fullIframe.style.left = `${0.5 * (width - (height / ratio))}px`;
        } else {
            fullIframe.style.width = `${width}px`;
            fullIframe.style.height = `${(width * ratio) + (height * ratio)}px`;
            fullIframe.style.top = `${-Math.abs(0.5 * (height * ratio))}px`;
            fullIframe.style.left = 0;
        }
    };

    const events = ['resize', 'orientationchange'];

    events.forEach((event) => {
        window.addEventListener(event, setVideoSize);
    });

    setVideoSize();
};

export const initYoutubePlayer = (wrapper) => {
    const tag = document.createElement('script');
    const firstScriptTag = document.getElementsByTagName('script')[0];
    let player = null;

    tag.src = 'https://www.youtube.com/iframe_api';
    firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);

    // eslint-disable-next-line func-names
    window.onYouTubeIframeAPIReady = function() {
        // eslint-disable-next-line no-undef
        player = new YT.Player(wrapper, {
            videoId   : `${wrapper.dataset.id}`,
            playerVars: {
                modestbranding: 1,
                autoplay      : 1,
                mute          : 1,
                controls      : 0,
                showinfo      : 0,
                wmode         : 'transparent',
                branding      : 0,
                rel           : 0,
                autohide      : 1,
                origin        : window.location.origin
            },
            events: {
                // eslint-disable-next-line
                'onReady': onPlayerReady,
                // eslint-disable-next-line
                'onStateChange': onStateChange
            }
        });

        // eslint-disable-next-line func-style
        function onPlayerReady(event) {
            // повторный initFullVideo() уже после инита плеера + ресайз
            const youtubeIframe = document.querySelector('.j-first-screen__youtube');

            initFullVideo(youtubeIframe);
            event.target.playVideo();

            const resizeEvents = ['resize', 'orientationchange'];

            resizeEvents.forEach((resizeEvent) => {
                window.addEventListener(resizeEvent, () => {
                    initFullVideo(youtubeIframe);
                });
            });
        }

        // eslint-disable-next-line func-style
        function onStateChange(event) {
            if (!event.data) {
                player.playVideo();
            }
        }
    };
};

// Инициализация селекта
export const initCustomSelect = (item) => {
    new CustomSelect({
        elem: item
    }).init();
};

// Табы
export const initTabs = (elemItem) => {
    new Tabs().init({
        elem: elemItem
    });
};

export const initExtraTabs = (options) => {
    new Tabs().init(options);
};

export const initHashTabs = (elemItem) => {
    new Tabs().init({
        elem: elemItem,
        hash: true
    });
};

// Параметрический
export const initParametric = (options) => {
    new Parametric(options).init();
};

export const initFlatCardOptions = () => {
    const optionsWrappers = Array.from(document.querySelectorAll('.j-flat-options-container:not([data-init])'));

    if (optionsWrappers.length) {
        optionsWrappers.forEach((optionsWrapper) => {
            const navigation = new FlatTicketOptions();

            navigation.init({
                target: optionsWrapper
            });

            optionsWrapper.setAttribute('data-init', 'true');
        });
    }
};

export const initFlatTicket = () => {
    const selector = '.j-flat-ticket:not([data-init]), .j-flat-ticket-promotion:not([data-init])';
    const flatCards = Array.from(document.querySelectorAll(selector));

    flatCards.forEach((elem) => {
        const card = new FlatTicket();

        card.init({elem});

        elem.setAttribute('data-init', 'true');
    });
};

initFlatTicket();

// Меню
export const initMenu = (element) => {
    return new Menu(element);
};

// Кнопка вызова меню
export const initMenuTrigger = (buttons) => {
    return new MenuTrigger(buttons);
};

// Слайдер на главной
export const initFullScreenSlider = (wrapper) => {
    return new FullScreenSlider(wrapper);
};

// Слайдеры на главной без родительского слайдера
export const initCircleSlider = (wrapper) => {
    return new CircleSlider(wrapper);
};

// Дефолтные туглеры
export const initToggle = (container) => {
    return new Toggle(container);
};

// Ипотечный калькулятор
export const initMortgageCalculator = (wrapper) => {
    const calculator = new MortgageCalculator({
        wrapper
    });

    calculator.init();
};

// Инициализация попапов
export const initPopup = (otherOptions = {}) => {
    const buttons = [...document.querySelectorAll('.j-popup:not([data-init])')];

    if (!buttons.length) {
        return;
    }

    const options = {
        ...{openButtons: buttons},
        ...otherOptions,
        onCreate: () => {
            initPopup();
        }
    };

    const facade = new PopupFacade(options);

    facade.init();

    buttons.forEach((element) => {
        element.setAttribute('data-init', 'true');
    });
};

// Инициализация форм
export const initForm = (wrapper, instance = null) => {
    new Form().init({
        target        : wrapper,
        popupInstance : instance,
        successMessage: true
    });
};

// Инициализация кастомного слайдера swiper slider
export const initSwiperSlider = (options) => {
    const wrapper = options.wrapper;
    const sliderWrapper = wrapper.querySelector('.j-swiper-slider__slides');
    const arrows = wrapper.dataset.arrows || false;
    const dots = wrapper.dataset.dots || false;
    const loop = wrapper.dataset.loop || false;
    const effect = wrapper.dataset.effect || 'slide';
    const otherParams = {};
    const initialSlide = options.activeSlide || 0;

    if (arrows) {
        const arrowLeft = wrapper.querySelector('.j-swiper-slider__arr-prev');
        const arrowRight = wrapper.querySelector('.j-swiper-slider__arr-next');

        otherParams.navigation = {
            prevEl: arrowLeft,
            nextEl: arrowRight
        };
    }

    if (dots) {
        const dotsWrapper = wrapper.querySelector('.j-swiper-slider__dots');

        otherParams.pagination = {
            el               : dotsWrapper,
            bulletActiveClass: 'is-active',
            bulletClass      : 'slider-swiper__dot',
            clickable        : true
        };
    }

    if (effect === 'fade') {
        otherParams.fadeEffect = {
            crossFade: true
        };
    }

    const slider = new SwiperSlider({
        item         : sliderWrapper,
        slidesPerView: 'auto',
        settings     : {
            wrapperClass        : 'j-swiper-slider__wrapper',
            slideClass          : 'j-swiper-slider__slide',
            slideActiveClass    : 'is-active',
            slideNextClass      : 'is-next',
            slidesPerView       : 'auto',
            updateOnWindowResize: true,
            loop,
            effect,
            initialSlide,
            ...otherParams
        }
    });

    slider.init();
};

export const initDesignSlider = (wrapper) => {
    return new DesignSlider(wrapper);
};

export const initPreloader = (wrapper) => {
    return new Preloader(wrapper);
};

// Инициализация кнопок избранного
export const initFavorite = (elems) => {
    const favorites = new FavoriteButton();

    favorites.init({
        favoriteButtons: elems
    });
};

observer.subscribe('favorite:reInit', () => {
    const favoriteButtons = Array.from(document.querySelectorAll('.j-favorite:not([data-init])'));

    initFavorite(favoriteButtons);
});

// Инициализация раздела избранного
export const initFavoritesList = (wrapper) => {
    const favorites = new FavoritesList();

    favorites.init({wrapper});
};

// Инициализация избранного в шапке
export const initFavoriteState = (wrapper) => {
    const favoriteState = new FavoriteState();

    favoriteState.init({wrapper});
};

export const initParallax = (wrapper) => {
    return new ParallaxMouse(wrapper);
};

// Инициализация акций и новостей
export const initOffersList = (wrapper) => {
    const offerList = new OffersList();

    offerList.init({wrapper});
};

export const initDetailsAccordion = (element) => {
    const detailsAccordion = new DetailsAccordion();

    detailsAccordion.init({element});
};

export const initConstructor = (scope) => {
    return new FlatConstructor({
        element: scope
    });
};

export const initPlanoplan = (options) => {
    const planoplan = new InitPlanoplan(options);

    planoplan.init();
};

export const initInfrastructure = () => {
    const infrastructure = new Infrastructure();

    infrastructure.init();
};

export const initWhiteBox = (options) => {
    const whiteBox = new WhiteBox(options);

    whiteBox.init();
};

// Сторонний виджет конструктора
export const initConstructorWidget = (options) => {
    return new ConstructorWidget(options);
};

// Запускаем видеоролики при скролле
export const initVideoOnScroll = (video) => {
    document.querySelector('body').click();

    const intObserver = new IntersectionObserver((entries) => {
        if (entries[0]['isIntersecting'] === true) {
            video.load();
            video.play();
        } else {
            video.pause();
        }
    });

    intObserver.observe(video);
};
